/**
 * Rules of the idle world that read only the content: costs, milestones, base stats and the
 * barn. No state is read or changed here; `effects.ts` combines these with the world's state.
 */
import type {
  IdleChainT,
  IdleContent,
  IdleGoalT,
  IdleMilestoneT,
  IdleProducerT,
  IdleUpgradeT,
} from '@hedgerow/data';

export const IDLE_HOUR_MS = 3_600_000;

/**
 * D316: the most any coin, rate, cost or Heritage figure may hold. Coins are plain doubles, which
 * overflow to Infinity past about 1.8e308; this stays well below, so sums of a few never overflow.
 */
export const IDLE_MAX = 1e300;

/** D316: keep a figure finite. Above `IDLE_MAX` it is held there; NaN reads 0. */
export function idleClamp(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(-IDLE_MAX, Math.min(IDLE_MAX, n));
}

interface IdleIndex {
  producers: Map<string, IdleProducerT>;
  chains: Map<string, IdleChainT>;
  upgrades: Map<string, IdleUpgradeT>;
  goals: Map<string, IdleGoalT>;
}

const indexes = new WeakMap<IdleContent, IdleIndex>();

/** Content looked up by id, built once per content object: the multiplier pipeline runs often. */
export function idleIndex(content: IdleContent): IdleIndex {
  let ix = indexes.get(content);
  if (!ix) {
    ix = {
      producers: new Map(content.producers.map((p) => [p.id, p])),
      chains: new Map(content.chains.map((c) => [c.id, c])),
      upgrades: new Map(content.upgrades.map((u) => [u.id, u])),
      goals: new Map([...content.goals, ...content.longGoals].map((g) => [g.id, g])),
    };
    indexes.set(content, ix);
  }
  return ix;
}

/** The producer with this id, or undefined. */
export function idleProducer(content: IdleContent, id: string): IdleProducerT | undefined {
  return idleIndex(content).producers.get(id);
}

/** The chain building with this id, or undefined. */
export function idleChain(content: IdleContent, id: string): IdleChainT | undefined {
  return idleIndex(content).chains.get(id);
}

/** The chain buildings that lift this producer. */
export function idleChainsFor(content: IdleContent, producer: string): IdleChainT[] {
  return content.chains.filter((c) => c.producer === producer);
}

/** Cost of the next one when `owned` are already owned (owned is at least 1). */
export function idleUnitCost(p: IdleProducerT, owned: number): number {
  return idleClamp(Math.round(p.baseCost * Math.pow(p.costGrowth, Math.max(0, owned - 1))));
}

/** Total cost of buying `n` more when `owned` are already owned. */
export function idleBuyCost(p: IdleProducerT, owned: number, n: number): number {
  let total = 0;
  for (let k = 0; k < n; k++) total += idleUnitCost(p, owned + k);
  return idleClamp(total);
}

/** The most that `coins` can buy when `owned` are already owned. */
export function idleBuyMax(p: IdleProducerT, owned: number, coins: number): number {
  let n = 0;
  let spent = 0;
  for (;;) {
    const next = idleUnitCost(p, owned + n);
    if (spent + next > coins || next >= IDLE_MAX) return n;
    spent += next;
    n++;
  }
}

/** The milestones reached with `owned` owned. */
export function idleMilestonesReached(p: IdleProducerT, owned: number): IdleMilestoneT[] {
  return p.milestones.filter((m) => owned >= m.owned);
}

/** The next milestone not yet reached, or undefined when every one is reached. */
export function idleNextMilestone(p: IdleProducerT, owned: number): IdleMilestoneT | undefined {
  return p.milestones.find((m) => owned < m.owned);
}

/** Time for one bar to fill at a speed factor. Whole milliseconds, at least 1. */
export function idleCycleMs(p: IdleProducerT, speed: number): number {
  return Math.max(1, Math.round(p.cycleMs / speed));
}

/** Coins one full bar pays with `owned` owned at a value factor. Whole coins. */
export function idleCyclePayout(p: IdleProducerT, owned: number, value: number): number {
  return idleClamp(Math.floor(p.payout * owned * value));
}

/** The barn's base capacity at `level` (1-based), in milliseconds. */
export function idleBarnBaseMs(content: IdleContent, level: number): number {
  const row = content.barn[Math.min(level, content.barn.length) - 1] ?? content.barn[0];
  return (row?.hours ?? 0) * IDLE_HOUR_MS;
}

/**
 * D361: the share of income past the barn's capacity that a barn at `level` still keeps, 0 for a
 * barn without overflow. Levels past a full day add it.
 */
export function idleBarnOverflow(content: IdleContent, level: number): number {
  const row = content.barn[Math.min(level, content.barn.length) - 1] ?? content.barn[0];
  return row?.overflow ?? 0;
}

/** Coins to raise the barn from `level` to the next, or undefined at the top level. */
export function idleBarnUpgradeCost(content: IdleContent, level: number): number | undefined {
  return content.barn[level]?.cost;
}

/**
 * D321: the power the Heritage bonus grows by, solved from the targets in the data: a festival is
 * worth holding (lifts income by `ready.lift`) once the generation's earnings have grown
 * `ready.earnings` times, so lift = earnings ^ (heritagePower x this).
 */
export function idleHeritageBonusPower(content: IdleContent): number {
  const f = content.festival;
  return Math.log(f.ready.lift) / (f.heritagePower * Math.log(f.ready.earnings));
}

/**
 * The income multiplier from Heritage earned (D249, D321): (1 + H / heritageBonusScale) to
 * `idleHeritageBonusPower`, so it grows more slowly than Heritage and every doubling of
 * Heritage past the scale lifts income by the same share.
 */
export function idleHeritageMultiplier(content: IdleContent, heritageEarned: number): number {
  if (heritageEarned <= 0) return 1;
  const f = content.festival;
  return idleClamp(
    Math.pow(1 + heritageEarned / f.heritageBonusScale, idleHeritageBonusPower(content)),
  );
}

/**
 * The coins a year must earn before the festival can be held: enough for `minYearEarned` and for
 * at least one Heritage, so "available" and "needs" always agree (D256).
 */
export function idleFestivalThreshold(content: IdleContent): number {
  return Math.max(content.festival.minYearEarned, idleCoinsForHeritage(content, 1));
}

/** The fewest coins that pay `heritage` Heritage. */
export function idleCoinsForHeritage(content: IdleContent, heritage: number): number {
  const f = content.festival;
  let need = Math.ceil((heritage / f.heritagePerRootCoin) ** (1 / f.heritagePower));
  while (idleHeritageFor(content, need) < heritage) need = Math.ceil(need * (1 + 1e-12)) + 1;
  while (need > 1 && idleHeritageFor(content, need - 1) >= heritage && need < 2 ** 53) need--;
  return need;
}

/**
 * Heritage earnings of `earned` are worth: a root of them, scaled (D245). One steady rule
 * (D299): at a power of 0.25, sixteen times the earnings doubles it, however large they are.
 */
export function idleHeritageFor(content: IdleContent, earned: number): number {
  const f = content.festival;
  return idleClamp(
    Math.floor(f.heritagePerRootCoin * Math.pow(Math.max(0, earned), f.heritagePower)),
  );
}

/**
 * D352: what a hand asks before hand-cost effects, with `festivals` festivals held this
 * generation: in the first year its own hand price, so a new world's first hour is unchanged
 * (as D323 had it); after a festival its own price or the price of its next `prices.handUnits`
 * units, whichever is greater. It moves only when that crop is bought, never with income or a
 * bonus.
 */
export function idleHandPrice(
  content: IdleContent,
  p: IdleProducerT,
  owned: number,
  festivals: number,
): number {
  if (festivals <= 0) return p.handCost;
  return idleClamp(Math.max(p.handCost, idleBuyCost(p, owned, content.prices.handUnits)));
}

/**
 * D333, D352: the least a hand may cost: `prices.handUnitShare` of the producer's next unit.
 * Hand-cost effects never cut below it.
 */
export function idleHandFloor(content: IdleContent, unitCost: number): number {
  return idleClamp(Math.round(unitCost * content.prices.handUnitShare));
}

/**
 * D352: a producer's unlock price with `festivals` festivals held this generation: its own price
 * times `prices.unlockPerFestival` for each. Festivals change only between years, so the price is
 * set when a year starts and holds all year; income and bonuses never move it.
 */
export function idleUnlockPrice(content: IdleContent, p: IdleProducerT, festivals: number): number {
  return idleClamp(
    Math.round(p.unlockCost * Math.pow(content.prices.unlockPerFestival, Math.max(0, festivals))),
  );
}

/**
 * D321: the Heritage earned that gives an income multiplier of `bonus`, the inverse of
 * `idleHeritageMultiplier`. 0 for a multiplier of 1 or less.
 */
export function idleHeritageForBonus(content: IdleContent, bonus: number): number {
  if (bonus <= 1) return 0;
  const f = content.festival;
  return idleClamp(
    f.heritageBonusScale * (Math.pow(bonus, 1 / idleHeritageBonusPower(content)) - 1),
  );
}
